import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";
import { debounce } from "lodash";
import uiConf from "../../config/uiConf";
import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import AutoForm from "../form/functional/AutoForm";
import "../grid/layout/DataGridMomSysOrderPurchase.css";
import layoutConf from "../../config/layoutConf";
import DialogHeaderWithClose from "../dialog/functional/DialogHeaderWithClose";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function EguOutSourcingMaterialReturnDetailRegisterDialog(props) {
    const {
        id,
        schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        outSourcingMaterialReturnMstSelect,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState({});
    const [keepOpen, setKeepOpen] = useState(false);

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const [outSourcingMaterialReturnDetailTargetGrid, setOutSourcingMaterialReturnDetailTargetGrid] = useState();
    const [outSourcingMaterialReturnDetailTargetSelect, setOutSourcingMaterialReturnDetailTargetSelect] = useState();
    const [outSourcingMaterialReturnDetailTargetInstance, setOutSourcingMaterialReturnDetailTargetInstance] =
        useState();

    if (schema && !schema.columnMap) {
        let columnMap = {};

        for (const column of schema.columns) {
            columnMap[column.name] = column;
        }

        schema.columnMap = columnMap;
    }

    useEffect(() => {
        let mounted = true;

        (async () => {
            if (outSourcingMaterialReturnDetailTargetGrid && mounted) {
                outSourcingMaterialReturnDetailTargetGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setOutSourcingMaterialReturnDetailTargetSelect(grid.selectedItems[0]);
                        } else {
                            setOutSourcingMaterialReturnDetailTargetSelect();
                        }
                    }, 100)
                );

                outSourcingMaterialReturnDetailTargetGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setOutSourcingMaterialReturnDetailTargetSelect(grid.selectedItems[0]);
                        } else {
                            setOutSourcingMaterialReturnDetailTargetSelect();
                        }
                    }, 100)
                );
            }
        })();

        return () => {
            mounted = false;
        };
    }, [outSourcingMaterialReturnDetailTargetGrid]);

    useEffect(() => {
        if (outSourcingMaterialReturnDetailTargetSelect) {
            setFormData((formData) => {
                let newFormData = {
                    ...formData,

                    materialReturnId: outSourcingMaterialReturnMstSelect.materialReturnId,
                    outSourcingId: outSourcingMaterialReturnDetailTargetSelect.outSourcingId,
                    outSourcingDetailId: outSourcingMaterialReturnDetailTargetSelect.outSourcingDetailId,

                    materialCode: outSourcingMaterialReturnDetailTargetSelect.materialCode,
                    materialCodeVer: outSourcingMaterialReturnDetailTargetSelect.materialCodeVer,
                    materialName: outSourcingMaterialReturnDetailTargetSelect.materialName,

                    outSourcingMaterialQty: outSourcingMaterialReturnDetailTargetSelect.outSourcingMaterialQty,
                    outSourcingShipQty: outSourcingMaterialReturnDetailTargetSelect.outSourcingShipQty,

                    consumeLotNo: outSourcingMaterialReturnDetailTargetSelect.consumeLotNo,
                };

                for (const column of schema.columns) {
                    if (column?.defaultValue) {
                        newFormData[column.name] = column.defaultValue;
                    }
                }

                return newFormData;
            });
        }
    }, [schema, outSourcingMaterialReturnMstSelect, outSourcingMaterialReturnDetailTargetSelect]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);
    };

    const notifyWarning = (msg) => {
        console.warn(msg);

        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        console.error(msg);

        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        if (outSourcingMaterialReturnDetailTargetInstance) {
            outSourcingMaterialReturnDetailTargetInstance.refresh();
        }

        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();

                if (onSuccess) {
                    setTimeout(() => onSuccess(result.data), 0);
                }
            } else {
                onNotifySuccess(result.data);

                if (onSuccess) {
                    setTimeout(() => onSuccess(result.data), 0);
                }
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        notifyFailure(
            t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
        );
    };

    const checkItemValidation = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("edit", column, column.validation, formData[column.name] || "");

            if (!valid) {
                notifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidation(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                notifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        // build base api url

        let url;

        url = `/api/${schema.name}`;

        let apiData = { ...formData };

        let form = new FormData();

        let json = {};
        let deleted = {};

        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;

                    form.append(formItem, file.file);
                }
            } else {
                if (apiData[formItem] !== undefined) {
                    json[formItem] = apiData[formItem];
                }
            }
        }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setFormData(null);

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <React.Fragment>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </React.Fragment>
        );
    };

    // 양식이 다음 틱에 알린 값을 바로 받는다 — 한 틱 더 미루면 그 사이 창이 채운 값 · 사용자가 고른 값을 양식의 옛 값이 덮는다
    const onChangeFormData = (formData) => {
        setOkButtonDisabled(false);

        setFormData(formData);
    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setFormData(null);

        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeaderWithClose>
                <DialogTitle>{t("dialog.title.EguOutSourcingMaterialReturnDetail.register")}</DialogTitle>
            </DialogHeaderWithClose>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.65}>
                        <BasicCrudScreenType
                            id="EguVwOutSourcingMaterialReturnDetailTarget"
                            view="EguVwOutSourcingMaterialReturnDetailTarget"
                            embededInOtherScreenType
                            headerTitle={t("term.materialReturnDetailTarget")}
                            noCreate
                            noEdit
                            noDelete
                            noExcel
                            onModifyViewUrl={(url) => `${url}/${outSourcingMaterialReturnMstSelect.outSourcingId}`}
                            onInitialized={(grid) => {
                                grid.selectionMode = "Row";
                                setOutSourcingMaterialReturnDetailTargetGrid(grid);
                            }}
                            onApiInitialized={(api) => {
                                setOutSourcingMaterialReturnDetailTargetInstance(api);
                            }}
                        />
                    </ReflexElement>

                    <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />

                    <ReflexElement flex={0.35}>
                        <div style={{ padding: "16px" }}>
                            <AutoForm
                                id="egu-outsourcing-material-return-detail-register-form"
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                //labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default EguOutSourcingMaterialReturnDetailRegisterDialog;
